/**
 * @file Shared React component — components/common/RichTextEditor.tsx.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import "./RichTextEditor.css";

type ActiveCommands = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  unorderedList: boolean;
  orderedList: boolean;
  link: boolean;
};

const INACTIVE_COMMANDS: ActiveCommands = {
  bold: false,
  italic: false,
  underline: false,
  unorderedList: false,
  orderedList: false,
  link: false,
};

function focusEditor(el: HTMLDivElement | null) {
  if (!el) return;
  el.focus();
}

function isSelectionInEditor(selection: Selection, editor: HTMLElement): boolean {
  const node = selection.anchorNode;
  return !!node && editor.contains(node);
}

function isSelectionInLink(selection: Selection, editor: HTMLElement): boolean {
  let node: Node | null = selection.anchorNode;
  while (node && node !== editor) {
    if (node.nodeName === "A") return true;
    node = node.parentNode;
  }
  return false;
}

/** React component — Rich Text Editor. */
export default function RichTextEditor({
  value,
  onChange,
  disabled,
  placeholder = "Write content…",
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastValueRef = useRef<string>("");
  const [activeCommands, setActiveCommands] = useState<ActiveCommands>(INACTIVE_COMMANDS);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Avoid clobbering cursor position if the value didn't change.
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    el.innerHTML = value || "";
  }, [value]);

  const updateActiveStates = useCallback(() => {
    if (disabled) {
      setActiveCommands(INACTIVE_COMMANDS);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !isSelectionInEditor(selection, el)) {
      setActiveCommands(INACTIVE_COMMANDS);
      return;
    }

    setActiveCommands({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      unorderedList: document.queryCommandState("insertUnorderedList"),
      orderedList: document.queryCommandState("insertOrderedList"),
      link: isSelectionInLink(selection, el),
    });
  }, [disabled]);

  useEffect(() => {
    document.addEventListener("selectionchange", updateActiveStates);
    return () => document.removeEventListener("selectionchange", updateActiveStates);
  }, [updateActiveStates]);

  const emitChange = () => {
    const el = ref.current;
    if (!el) return;
    const html = el.innerHTML || "";
    lastValueRef.current = html;
    onChange(html);
    updateActiveStates();
  };

  const cmd = (command: string, commandValue?: string) => {
    focusEditor(ref.current);
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const insertLink = () => {
    focusEditor(ref.current);
    const url = window.prompt("Enter URL (https://...)");
    if (!url) return;
    cmd("createLink", url);
  };

  const toolbarButtonClass = (active: boolean) =>
    ["rte-btn", active ? "rte-btn--active" : ""].filter(Boolean).join(" ");

  return (
    <div className="rte">
      <div className="rte-toolbar" onMouseDown={(e) => e.preventDefault()}>
        <button
          className={toolbarButtonClass(activeCommands.bold)}
          type="button"
          onClick={() => cmd("bold")}
          disabled={disabled}
          aria-pressed={activeCommands.bold}
          title="Bold"
        >
          <span className="rte-btn-label rte-btn-label--bold">B</span>
        </button>
        <button
          className={toolbarButtonClass(activeCommands.italic)}
          type="button"
          onClick={() => cmd("italic")}
          disabled={disabled}
          aria-pressed={activeCommands.italic}
          title="Italic"
        >
          <span className="rte-btn-label rte-btn-label--italic">I</span>
        </button>
        <button
          className={toolbarButtonClass(activeCommands.underline)}
          type="button"
          onClick={() => cmd("underline")}
          disabled={disabled}
          aria-pressed={activeCommands.underline}
          title="Underline"
        >
          <span className="rte-btn-label rte-btn-label--underline">U</span>
        </button>
        <button
          className={toolbarButtonClass(activeCommands.unorderedList)}
          type="button"
          onClick={() => cmd("insertUnorderedList")}
          disabled={disabled}
          aria-pressed={activeCommands.unorderedList}
          title="Bullet list"
        >
          • List
        </button>
        <button
          className={toolbarButtonClass(activeCommands.orderedList)}
          type="button"
          onClick={() => cmd("insertOrderedList")}
          disabled={disabled}
          aria-pressed={activeCommands.orderedList}
          title="Numbered list"
        >
          1. List
        </button>
        <button
          className={toolbarButtonClass(activeCommands.link)}
          type="button"
          onClick={insertLink}
          disabled={disabled}
          aria-pressed={activeCommands.link}
          title="Insert link"
        >
          Link
        </button>
        <button
          className="rte-btn"
          type="button"
          onClick={() => {
            cmd("unlink");
            cmd("removeFormat");
          }}
          disabled={disabled}
          title="Clear formatting"
        >
          Clear
        </button>
      </div>

      <div
        ref={ref}
        className="rte-editor"
        contentEditable={!disabled}
        suppressContentEditableWarning={true}
        onInput={emitChange}
        onBlur={emitChange}
        onKeyUp={updateActiveStates}
        onMouseUp={updateActiveStates}
        onFocus={updateActiveStates}
        data-placeholder={placeholder}
        aria-label="Rich text editor"
      />
    </div>
  );
}
